import { generatePath, matchPath, useLocation, useParams } from 'react-router'
import { usePrefCurrentWorkspace, useUser } from 'utils'
import { useFacility } from '../components/Facility/FacilityProvider'
import { useTemplate } from '../components/template'
import { useEvent } from './eventUtils'
import { RouteName, RoutePath } from './routing'

export function useCurrentPage() {
  const location = useLocation()
  const match = (path: string) => matchPath(path, location.pathname)

  if (match(RoutePath.Content)) return { path: RouteName.Content, isChild: false }
  if (match(RoutePath.Developer)) return { path: RouteName.Developer, isChild: false }
  if (match(RoutePath.Events)) return { path: RouteName.Events, isChild: false }
  if (match(RoutePath.EventInfo))
    return { path: RouteName.EventInfo, isChild: true, parentPath: RouteName.Events }
  if (match(RoutePath.Facilities)) return { path: RouteName.Facilities, isChild: false }
  if (match(RoutePath.FacilityInfo))
    return { path: RouteName.Facilities, isChild: true, parentPath: RouteName.Facilities }
  if (match(RoutePath.Intelligence)) return { path: RouteName.Intelligence, isChild: false }
  if (match(RoutePath.Integrations)) return { path: RouteName.Integrations, isChild: false }
  if (match(RoutePath.ManageWorkspace)) return { path: RouteName.ManageWorkspace, isChild: false }
  if (match(RoutePath.ManageWorkspaceBranding))
    return { path: RouteName.ManageWorkspaceBranding, isChild: false }
  if (match(RoutePath.ManageWorkspaceUsers))
    return { path: RouteName.ManageWorkspaceUsers, isChild: false }
  if (match(RoutePath.ManageMedia)) return { path: RouteName.Media, isChild: false }
  if (match(RoutePath.OnTheGoMember))
    return { isChild: true, parentPath: RouteName.OnTheGo, path: RouteName.OnTheGoMember }
  if (match(RoutePath.OnTheGo)) return { path: RouteName.OnTheGo, isChild: false }
  if (match(RoutePath.Properties)) return { path: RouteName.Properties, isChild: false }
  if (match(RoutePath.RegistrationConsents))
    return { path: RouteName.RegistrationConsents, isChild: false }
  if (match(RoutePath.RegistrationConsentInfo))
    return {
      path: RouteName.RegistrationConsents,
      isChild: true,
      parentPath: RouteName.RegistrationConsents,
    }
  if (match(RoutePath.Subscription)) return { path: RouteName.Subscription, isChild: false }
  if (match(RoutePath.Templates)) return { path: RouteName.Templates, isChild: false }
  if (match(RoutePath.TemplateInfo))
    return { path: RouteName.Templates, isChild: true, parentPath: RouteName.Templates }
  if (match(RoutePath.Text)) return { path: RouteName.Text, isChild: false }
  if (match(RoutePath.TextInfo)) return { path: RouteName.Text, isChild: true }
  if (match(RoutePath.Tool)) return { path: RouteName.Tool, isChild: false }
  if (match(RoutePath.ToolInfo)) return { path: RouteName.Tool, isChild: true }
  if (match(RoutePath.Trait)) return { path: RouteName.Trait, isChild: false }
  if (match(RoutePath.TraitInfo)) return { path: RouteName.Trait, isChild: true }
  if (match(RoutePath.WorkspaceAnalytics)) return { path: RouteName.Analytics, isChild: false }
}

export function isContentPage(routeName?: RouteName) {
  switch (routeName) {
    case RouteName.Content:
    case RouteName.Media:
    case RouteName.RegistrationConsents:
    case RouteName.Templates:
    case RouteName.Text:
    case RouteName.Tool:
    case RouteName.Trait:
      return true
    default:
      return false
  }
}

export function isSettingsPage(routeName?: RouteName) {
  switch (routeName) {
    case RouteName.ManageWorkspace:
    case RouteName.ManageWorkspaceBranding:
    case RouteName.ManageWorkspaceUsers:
    case RouteName.Subscription:
    case RouteName.Integrations:
    case RouteName.Intelligence:
    case RouteName.Developer:
    case RouteName.Properties:
      return true
    default:
      return false
  }
}

export function usePageInfo(routeName?: RouteName) {
  const [workspaceId] = usePrefCurrentWorkspace()
  const { id } = useParams()
  const memberUser = useUser(id)
  const { event } = useEvent()
  const { facility } = useFacility()
  const { template } = useTemplate()

  switch (routeName) {
    case RouteName.Analytics:
      return {
        title: 'Analytics',
        identifier: 'Analytics',
        path: ' ',
      }
    case RouteName.Events:
      return {
        title: 'Events',
        identifier: 'Event',
        path: generatePath(RoutePath.Events, { workspaceId: workspaceId }),
      }
    case RouteName.EventInfo:
      if (!event) return { title: 'Events', identifier: 'Event Detail' }
      else
        return {
          title: 'Events',
          identifier: `${event.title}`,
          path: generatePath(RoutePath.EventInfo, { workspaceId: workspaceId, id: event.id }),
        }
    case RouteName.Facilities:
      if (!facility) return { title: 'Facilities', identifier: 'Facility' }
      return {
        title: 'Facilities',
        identifier: `${facility.title}`,
        path: generatePath(RoutePath.Facilities, { workspaceId: workspaceId }),
      }
    case RouteName.OnTheGo:
      return {
        title: 'On-the-Go',
        identifier: 'On-the-Go',
        path: generatePath(RoutePath.OnTheGo, {
          workspaceId: workspaceId,
        }),
      }
    case RouteName.OnTheGoMember:
      if (!memberUser) return { title: 'On-the-Go', identifier: 'Member' }
      return {
        title: 'On-the-Go',
        identifier: memberUser.displayName || memberUser.email || 'Member',
        path: generatePath(RoutePath.OnTheGoMember, {
          workspaceId: workspaceId,
          id: memberUser.id,
        }),
      }

    case RouteName.RegistrationConsents:
      return {
        title: 'Registration Consents',
        identifier: 'Consent',
        path: generatePath(RoutePath.RegistrationConsents, { workspaceId: workspaceId }),
      }
    case RouteName.Templates:
      return {
        title: 'Templates',
        identifier: template?.title ?? 'Template',
        path: generatePath(RoutePath.Templates, { workspaceId: workspaceId }),
      }
    case RouteName.Trait:
      return {
        title: 'Traits',
        identifier: 'Trait',
        path: generatePath(RoutePath.Trait, { workspaceId: workspaceId }),
      }
  }

  if (isContentPage(routeName)) {
    return {
      title: 'Content',
      identifier: 'Content',
      path: generatePath(RoutePath.Content, { workspaceId: workspaceId }),
    }
  }
}
